import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Typography, Chip } from "@mui/material";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import type { Payment } from "../../Graphql/types/payment";
import DateRangeSelector from "../common/DateRangeSelector";
import AdminScopeFilters, {
  ALL_BUSINESS_TYPES,
  ALL_COUNTRIES,
  AdminScopeFilterLegend,
} from "../common/AdminScopeFilters";
import AdminActiveFilterChips from "../common/AdminActiveFilterChips";
import AdminPageShell from "../common/AdminPageShell";
import AdminFilterBar from "../common/AdminFilterBar";
import AdminSearchField from "../common/AdminSearchField";
import AdminDataTable, {
  type AdminTableColumn,
} from "../common/AdminDataTable";
import {
  formatAdminMoney,
  labelForAdminCountry,
} from "../common/adminMarketCountries";
import {
  formatPaymentMethodLabel,
  formatPaymentStatusLabel,
} from "../common/adminEnumLabels";
import { useListAllPaymentsSuperAdmin } from "../../hooks/api/usePayment";
import { getPaymentDetailPath } from "../../constants/routes";

const PaymentList: React.FC = () => {
  const navigate = useNavigate();
  const [paymentList, setPaymentList] = useState<Payment[]>([]);
  const [search, setSearch] = useState("");
  const [countryCode, setCountryCode] = useState(ALL_COUNTRIES);
  const [businessType, setBusinessType] = useState(ALL_BUSINESS_TYPES);
  const [dateRange, setDateRange] = useState<{
    startDate: Date;
    endDate: Date;
  } | null>(null);

  const getPaymentListMutation = useListAllPaymentsSuperAdmin();

  const loadPayments = useCallback(() => {
    getPaymentListMutation
      .mutateAsync({
        dateFrom: dateRange
          ? format(dateRange.startDate, "yyyy-MM-dd")
          : undefined,
        dateTo: dateRange ? format(dateRange.endDate, "yyyy-MM-dd") : undefined,
        countryCode: countryCode || undefined,
        businessType: businessType || undefined,
      })
      .then((res) =>
        setPaymentList(res.listAllPaymentsSuperAdmin.paymentList ?? []),
      )
      .catch(() => {});
  }, [dateRange, countryCode, businessType]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const filteredList = useMemo(() => {
    if (!search.trim()) return paymentList;
    return paymentList.filter((payment) =>
      Object.values(payment)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase()),
    );
  }, [paymentList, search]);

  const getStatusColor = (
    status: string,
  ):
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning" => {
    switch (status?.toUpperCase()) {
      case "COMPLETED":
      case "SUCCESS":
        return "success";
      case "PENDING":
        return "warning";
      case "FAILED":
        return "error";
      default:
        return "default";
    }
  };

  const columns: AdminTableColumn<Payment>[] = [
    {
      id: "paymentId",
      header: "Payment ID",
      mobilePrimary: true,
      renderCell: (payment) => (
        <Typography variant="body2" fontFamily="monospace" fontSize="0.8125rem">
          {payment.paymentId}
        </Typography>
      ),
    },
    {
      id: "country",
      header: "Country",
      renderCell: (payment) =>
        payment.countryCode ? labelForAdminCountry(payment.countryCode) : "—",
    },
    {
      id: "status",
      header: "Status",
      renderCell: (payment) => (
        <Chip
          label={formatPaymentStatusLabel(payment.paymentStatus)}
          color={getStatusColor(payment.paymentStatus)}
          size="small"
        />
      ),
    },
    {
      id: "method",
      header: "Method",
      renderCell: (payment) => formatPaymentMethodLabel(payment.paymentMethod),
    },
    {
      id: "amount",
      header: "Amount",
      align: "right",
      renderCell: (payment) =>
        formatAdminMoney(payment.amount ?? 0, payment.currency ?? "RWF"),
    },
  ];

  const isLoading =
    getPaymentListMutation.isPending && paymentList.length === 0;

  return (
    <AdminPageShell
      title="Payments"
      description={<AdminScopeFilterLegend />}
      meta={
        isLoading
          ? "Loading…"
          : `${filteredList.length} ${filteredList.length === 1 ? "payment" : "payments"}`
      }
      toolbar={
        <AdminFilterBar
          search={
            <AdminSearchField
              placeholder="Search payments…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          }
          filters={
            <AdminScopeFilters
              values={{ countryCode, businessType }}
              onChange={({ countryCode: c, businessType: b }) => {
                setCountryCode(c);
                setBusinessType(b);
              }}
              periodControl={
                <DateRangeSelector
                  compact
                  value={dateRange}
                  onChange={setDateRange}
                />
              }
            />
          }
        />
      }
      filterChips={
        <AdminActiveFilterChips
          values={{ countryCode, businessType }}
          onClearCountry={() => setCountryCode(ALL_COUNTRIES)}
          onClearBusinessType={() => setBusinessType(ALL_BUSINESS_TYPES)}
          onClearAll={() => {
            setCountryCode(ALL_COUNTRIES);
            setBusinessType(ALL_BUSINESS_TYPES);
          }}
          extra={
            dateRange ? (
              <Chip
                size="small"
                label={`Date: ${format(dateRange.startDate, "MMM d, yyyy")} – ${format(dateRange.endDate, "MMM d, yyyy")}`}
                onDelete={() => setDateRange(null)}
              />
            ) : undefined
          }
        />
      }
    >
      <AdminDataTable
        columns={columns}
        rows={filteredList}
        getRowId={(p) => p.paymentId}
        onRowClick={(p) => navigate(getPaymentDetailPath(p.paymentId))}
        emptyMessage="No payments found"
        loading={isLoading}
      />
    </AdminPageShell>
  );
};

export default PaymentList;
